import type { MuxClient } from "@mux/sdk/v2/client"
import { controlApiFetch } from "@/transport/control-api"
import { jsonArray, jsonObject, jsonString } from "@/transport/decode"
import { expectJson, expectResponse } from "@/transport/http"
import { sdkRequest, sdkResultError } from "@/transport/sdk-request"

export type CapabilityKind = "skill" | "tool" | "command" | "language-server"
/** Kinds an agent can load; slash commands are invoked only by the user. */
export type AgentCapabilityKind = Exclude<CapabilityKind, "command" | "language-server">
export type CapabilityScope = "builtin" | "user"
export type CapabilityMode = "auto" | "manual" | "disabled"
export type CapabilityAvailability = { mode?: CapabilityMode; projects?: Record<string, CapabilityMode> }

export type CapabilitySummary = {
  id: string
  kind: CapabilityKind
  title: string
  summary: string
  scope: CapabilityScope
  availability?: CapabilityAvailability
  /** An account's customization of a built-in; deleting it restores the default. */
  overrides_builtin?: boolean
  /** A built-in the account can replace with its own version. */
  customizable?: boolean
}

export type LoadedCapability = CapabilitySummary & {
  kind: AgentCapabilityKind
  prompt: string
}

export type CapabilityRuntime = {
  url?: string
  type: string
  command?: string
  args?: string[]
}

export type CapabilityOperation = {
  name: string
  description?: string
  inputs?: Record<string, unknown>
  output?: Record<string, unknown>
}

export type CapabilityActivation = { checks: string[]; install: string[]; auth_checks: string[] }
export type CapabilityAuthentication = { strategy: string; adapter: string }

export type LanguageServerDefinition = {
  command: string
  args?: string[]
  languages: { language_id: string; patterns: string[] }[]
  root_markers?: string[]
  initialization?: Record<string, unknown>
  settings?: Record<string, unknown>
  npm?: { package: string; version: string }
  source?: string
}

export type CapabilityDetail = CapabilitySummary & {
  language_server?: LanguageServerDefinition
  version: number
  keywords?: string[]
  resolved_instructions: string
  uses?: string[]
  runtime?: CapabilityRuntime
  operations?: CapabilityOperation[]
  activation?: CapabilityActivation
  authentication?: CapabilityAuthentication
  inputs?: Record<string, unknown>
  output?: Record<string, unknown>
  permissions?: string[]
  /** Text files a tool bundles, keyed by relative path. */
  files?: Record<string, string>
  steps?: string[]
  source_path?: string
  editable: boolean
  revision: string
}

export type CapabilityDraft = {
  language_server?: LanguageServerDefinition
  id: string
  kind: CapabilityKind
  title: string
  summary: string
  scope: "user"
  availability?: CapabilityAvailability
  /** Set when saving replaces a built-in command default. */
  overrides_builtin?: boolean
  revision: string
  instructions: string
  keywords: string[]
  uses: string[]
  runtime?: CapabilityRuntime
  operations?: CapabilityOperation[]
  activation?: CapabilityActivation
  authentication?: CapabilityAuthentication
  inputs?: Record<string, unknown>
  output?: Record<string, unknown>
  permissions: string[]
  files?: Record<string, string>
  /** Slash command prompts, sent one turn at a time. */
  steps?: string[]
}

function decodeSummary(value: unknown): CapabilitySummary {
  const item = jsonObject(value, "Server returned an invalid capability")
  const kind = jsonString(item.kind, "Server returned an invalid capability")
  const scope = jsonString(item.scope, "Server returned an invalid capability")
  if (kind !== "skill" && kind !== "tool" && kind !== "command" && kind !== "language-server") throw new Error("Server returned an invalid capability kind")
  if (scope !== "builtin" && scope !== "user") throw new Error("Server returned an invalid capability scope")
  return {
    id: jsonString(item.id, "Server returned an invalid capability"),
    kind,
    title: jsonString(item.title, "Server returned an invalid capability"),
    summary: jsonString(item.summary, "Server returned an invalid capability"),
    scope,
    availability: decodeAvailability(item.availability),
    overrides_builtin: item.overrides_builtin === true,
    customizable: item.customizable === true,
  }
}

function decodeAvailability(raw: unknown): CapabilityAvailability | undefined {
  if (raw === undefined || raw === null) return
  const value = jsonObject(raw, "Invalid capability availability")
  const mode = (raw: unknown): CapabilityMode => {
    if (raw === "auto" || raw === "manual" || raw === "disabled") return raw
    throw new Error("Invalid capability availability mode")
  }
  return {
    mode: value.mode === undefined || value.mode === "" ? "auto" : mode(value.mode),
    projects: value.projects == null ? undefined : Object.fromEntries(Object.entries(jsonObject(value.projects, "Invalid project overrides")).map(([id, value]) => [id, mode(value)])),
  }
}

function optionalStrings(value: unknown, message: string) {
  if (value === undefined || value === null) return undefined
  return jsonArray(value, message).map((item) => jsonString(item, message))
}

export function decodeCapabilityOperations(value: unknown): CapabilityOperation[] | undefined {
  if (value === undefined || value === null) return undefined
  return jsonArray(value, "Operations must be a JSON array").map((raw) => {
    const item = jsonObject(raw, "Invalid operation")
    return {
      name: jsonString(item.name, "Operation name is required"),
      description: item.description === undefined ? undefined : jsonString(item.description, "Invalid operation description"),
      inputs: item.inputs === undefined || item.inputs === null ? undefined : jsonObject(item.inputs, "Invalid operation inputs"),
      output: item.output === undefined || item.output === null ? undefined : jsonObject(item.output, "Invalid operation output"),
    }
  })
}

export function decodeLanguageServerDefinition(raw: unknown): LanguageServerDefinition {
  const item = jsonObject(raw, "Invalid language-server definition")
  const known = (value: Record<string, unknown>, keys: string[]) => {
    for (const key of Object.keys(value)) if (!keys.includes(key)) throw new Error(`Unexpected language-server field: ${key}`)
  }
  known(item, ["command", "args", "languages", "root_markers", "initialization", "settings", "npm", "source"])
  const npm = item.npm == null ? undefined : jsonObject(item.npm, "Invalid npm recipe")
  if (npm) known(npm, ["package", "version"])
  return {
    command: jsonString(item.command, "Executable name is required"),
    args: optionalStrings(item.args, "Invalid server arguments"),
    languages: jsonArray(item.languages, "Language selectors are required").map(raw => {
      const language = jsonObject(raw, "Invalid language selector")
      known(language, ["language_id", "patterns"])
      return { language_id: jsonString(language.language_id, "Language ID is required"), patterns: jsonArray(language.patterns, "Filename patterns are required").map(pattern => jsonString(pattern, "Invalid pattern")) }
    }),
    root_markers: optionalStrings(item.root_markers, "Invalid root markers"),
    initialization: item.initialization == null ? undefined : jsonObject(item.initialization, "Invalid initialization options"),
    settings: item.settings == null ? undefined : jsonObject(item.settings, "Invalid settings"),
    npm: npm && { package: jsonString(npm.package, "Package name is required"), version: jsonString(npm.version, "Exact version is required") },
    source: item.source == null ? undefined : jsonString(item.source, "Invalid source URL"),
  }
}

export function decodeDetail(value: unknown): CapabilityDetail {
  const item = jsonObject(value, "Server returned an invalid capability")
  const summary = decodeSummary(item)
  const runtimeValue = item.runtime === undefined || item.runtime === null ? undefined : jsonObject(item.runtime, "Server returned an invalid capability runtime")
  return {
    ...summary,
    language_server: item.language_server == null ? undefined : decodeLanguageServerDefinition(item.language_server),
    version: typeof item.version === "number" ? item.version : 1,
    keywords: optionalStrings(item.keywords, "Server returned invalid capability keywords"),
    resolved_instructions: jsonString(item.resolved_instructions, "Server returned invalid capability instructions"),
    uses: optionalStrings(item.uses, "Server returned invalid capability dependencies"),
    runtime: runtimeValue ? {
      type: jsonString(runtimeValue.type, "Server returned an invalid capability runtime"),
      url: typeof runtimeValue.url === "string" ? runtimeValue.url : undefined,
      command: typeof runtimeValue.command === "string" ? runtimeValue.command : undefined,
      args: optionalStrings(runtimeValue.args, "Server returned invalid capability arguments"),
    } : undefined,
    operations: decodeCapabilityOperations(item.operations),
    activation: item.activation ? (() => { const value = jsonObject(item.activation, "Invalid activation"); return { checks: optionalStrings(value.checks, "Invalid checks") ?? [], install: optionalStrings(value.install, "Invalid install commands") ?? [], auth_checks: optionalStrings(value.auth_checks, "Invalid auth checks") ?? [] } })() : undefined,
    authentication: item.authentication ? (() => { const value = jsonObject(item.authentication, "Invalid authentication"); return { strategy: jsonString(value.strategy, "Invalid authentication strategy"), adapter: jsonString(value.adapter, "Invalid authentication adapter") } })() : undefined,
    inputs: item.inputs === undefined || item.inputs === null ? undefined : jsonObject(item.inputs, "Server returned invalid capability inputs"),
    output: item.output === undefined || item.output === null ? undefined : jsonObject(item.output, "Server returned invalid capability output"),
    permissions: optionalStrings(item.permissions, "Server returned invalid capability permissions"),
    files: item.files === undefined || item.files === null ? undefined : Object.fromEntries(Object.entries(jsonObject(item.files, "Server returned invalid bundled files")).map(([name, content]) => [name, jsonString(content, "Server returned an invalid bundled file")])),
    steps: optionalStrings(item.steps, "Server returned invalid command steps"),
    source_path: typeof item.source_path === "string" ? item.source_path : undefined,
    editable: item.editable === true,
    revision: jsonString(item.revision, "Server returned an invalid capability revision"),
  }
}

export async function listCapabilities(signal?: AbortSignal) {
  return expectJson(await controlApiFetch("/api/capabilities", { signal }), (value) => jsonArray(value, "Server returned invalid capabilities").map(decodeSummary))
}

export async function getCapability(id: string, signal?: AbortSignal) {
  return expectJson(await controlApiFetch(`/api/capabilities/${encodeURIComponent(id)}/detail`, { signal }), decodeDetail)
}

export async function saveCapability(draft: CapabilityDraft) {
  const runtime = draft.kind === "tool" ? draft.runtime : undefined
  // Commands carry only their prompts; the schema rejects every other field.
  const manifest = draft.kind === "language-server"
    ? { version: 1, id: draft.id, kind: draft.kind, title: draft.title, summary: draft.summary, language_server: draft.language_server }
    : draft.kind === "command"
    ? { version: 1, id: draft.id, kind: draft.kind, title: draft.title, summary: draft.summary, steps: draft.steps ?? [] }
    : {
        version: 1,
        id: draft.id,
        kind: draft.kind,
        title: draft.title,
        summary: draft.summary,
        availability: draft.availability,
        keywords: draft.keywords,
        uses: draft.uses,
        runtime,
        operations: draft.kind === "tool" ? draft.operations : undefined,
        activation: draft.activation,
        authentication: draft.authentication,
        inputs: draft.inputs,
        output: draft.output,
        permissions: draft.permissions,
        files: draft.kind === "tool" ? draft.files : undefined,
      }
  return expectJson(await controlApiFetch(`/api/capabilities/${encodeURIComponent(draft.id)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      revision: draft.revision,
      instructions: draft.kind === "command" || draft.kind === "language-server" ? "" : draft.instructions,
      manifest,
    }),
  }), (raw) => {
    const receipt = jsonObject(raw, "Invalid capability save receipt; reload before retrying")
    const record = jsonObject(receipt.record, "Invalid capability save receipt; reload before retrying")
    const manifest = jsonObject(record.manifest, "Invalid saved capability")
    return decodeDetail({ ...manifest, scope: "user", editable: true, revision: receipt.revision, resolved_instructions: record.instructions, overrides_builtin: draft.overrides_builtin })
  })
}

export async function deleteCapability(id: string, revision: string) {
  await expectResponse(await controlApiFetch(`/api/capabilities/${encodeURIComponent(id)}?revision=${encodeURIComponent(revision)}`, { method: "DELETE" }))
}

export async function listCapabilityProjects(client: MuxClient) {
  const result = await sdkRequest(client, "get", { url: "/project" })
  if (!result.data) throw sdkResultError(result, "Could not load projects")
  return jsonArray(result.data, "Invalid projects").map((raw) => {
    const project = jsonObject(raw, "Invalid project")
    const id = jsonString(project.id, "Invalid workspace ID")
    return { id, name: typeof project.name === "string" ? project.name : id }
  })
}

function decodeAgentSummary(value: unknown): CapabilitySummary & { kind: AgentCapabilityKind } {
  const summary = decodeSummary(value)
  if (summary.kind === "command" || summary.kind === "language-server") throw new Error("Server returned a non-agent definition as an agent capability")
  return { ...summary, kind: summary.kind }
}

/** Everything the user can attach with $; the composer filters it locally while typing. */
export async function listSelectableCapabilities(client: MuxClient, signal?: AbortSignal) {
  const result = await sdkRequest(client, "get", { url: "/api/capabilities", signal })
  if (!result.data) throw sdkResultError(result, "Could not load capabilities")
  return jsonArray(result.data, "Server returned invalid capabilities").map((raw) => ({
    ...decodeAgentSummary(raw),
    keywords: optionalStrings(jsonObject(raw, "Server returned an invalid capability").keywords, "Server returned invalid capability keywords") ?? [],
  }))
}

export async function loadCapability(client: MuxClient, id: string) {
  const result = await sdkRequest(client, "get", { url: `/api/capabilities/${encodeURIComponent(id)}` })
  if (!result.data) throw sdkResultError(result, "Could not load capability")
  const item = jsonObject(result.data, "Server returned an invalid capability")
  return { ...decodeAgentSummary(item), prompt: jsonString(item.prompt, "Server returned an invalid capability") } satisfies LoadedCapability
}
